import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Switch,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { ReactNode, useEffect } from 'react';
import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';
import {
  ClinicalReportAnamnesisDtoPreviousDiagnoseStatus,
  ClinicalReportAnamnesisDtoPreviousTherapyStatus,
  TherapyDto,
} from '../../../../../api/generated/model';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { getPreviousDiagnoseStatusName, getPreviousTherapyStatusName } from '../../../../../utils/anamnesis.ts';
import {
  addDateMonths,
  formatDateForInput,
  getDateAfterInputValue,
  isInputDateValue,
} from '../../../../../utils/date.ts';
import { SelectMedication } from '../SelectMedication.tsx';

interface ClinicalReportAnamnesisTherapyFormProps<TFieldValues extends FieldValues> {
  previousDiagnose: FieldPath<TFieldValues>;
  previousDiagnoseDate: FieldPath<TFieldValues>;
  previousDiagnoseDateUnknown: FieldPath<TFieldValues>;
  previousTherpy: FieldPath<TFieldValues>;
  previousTherpyDate: FieldPath<TFieldValues>;
  previousTherpyDateUnknown: FieldPath<TFieldValues>;
  startPhase1: FieldPath<TFieldValues>;
  therapyEnd: FieldPath<TFieldValues>;
  therapyMedication: FieldPath<TFieldValues>;
  death: FieldPath<TFieldValues>;
  children?: ReactNode;
  therapy: TherapyDto;
}
export function ClinicalReportAnamnesisTherapyForm<TFieldValues extends FieldValues>({
  previousDiagnose,
  previousDiagnoseDate,
  previousDiagnoseDateUnknown,
  previousTherpy,
  previousTherpyDate,
  previousTherpyDateUnknown,
  startPhase1,
  therapyEnd,
  therapyMedication,
  death,
  children,
  therapy,
}: ClinicalReportAnamnesisTherapyFormProps<TFieldValues>) {
  const {
    control,
    watch,
    getFieldState,
    setValue,
    formState: { errors },
  } = useFormContext<TFieldValues>();

  const previousDiagnoseValue = watch(previousDiagnose);
  const previousDiagnoseDateUnknownValue = watch(previousDiagnoseDateUnknown);
  const previousTherapyValue = watch(previousTherpy);
  const previousTherapyDateUnknownValue = watch(previousTherpyDateUnknown);
  const startPhase1Value = watch(startPhase1);
  const therapyEndValue = watch(therapyEnd);
  const therapyMedicationValue = watch(therapyMedication);

  const isStartPhase1Required =
    getFieldState(startPhase1).isDirty || getFieldState(therapyEnd).isDirty || getFieldState(therapyMedication).isDirty;

  const minTherapyEnd =
    getDateAfterInputValue(therapy.startPhase2 ? formatDateForInput(therapy.startPhase2) : undefined) ??
    (typeof startPhase1Value === 'string' ? getDateAfterInputValue(startPhase1Value) : undefined);

  useEffect(() => {
    if (typeof startPhase1Value === 'string' && isInputDateValue(startPhase1Value) && !therapyEndValue) {
      const endDate = addDateMonths(6, startPhase1Value);
      if (endDate) {
        setValue(therapyEnd, formatDateForInput(endDate) as PathValue<TFieldValues, typeof therapyEnd>);
      }
    }
  }, [startPhase1Value, therapyEndValue]);
  return (
    <Stack gap={2}>
      <Typography level={'title-lg'}>Anamnese</Typography>
      <Stack direction={'row'} gap={2}>
        <Stack flex={1} justifyContent={'space-between'}>
          <FormControl>
            <FormLabel>
              <Typography>Wurde jemals eine TB diagnostiziert?</Typography>
            </FormLabel>
            <Controller
              name={previousDiagnose}
              control={control}
              render={({ field: { onChange, value } }) => (
                <ToggleButtonGroup value={value} spacing={1}>
                  {Object.values(ClinicalReportAnamnesisDtoPreviousDiagnoseStatus).map((status) => (
                    <Button value={status} key={status} onClick={() => onChange(status)}>
                      {getPreviousDiagnoseStatusName(status)}
                    </Button>
                  ))}
                </ToggleButtonGroup>
              )}
            />
          </FormControl>
          <Stack gap={1}>
            <FormControl error={!!errors.previousDiagnoseDate}>
              <FormLabel>
                <Typography>Datum der Diagnose</Typography>
              </FormLabel>
              <GermanDateInput
                control={control}
                name={previousDiagnoseDate}
                disabled={
                  previousDiagnoseValue !== ClinicalReportAnamnesisDtoPreviousDiagnoseStatus.YES ||
                  previousDiagnoseDateUnknownValue === true
                }
                rules={{
                  required:
                    previousDiagnoseValue === ClinicalReportAnamnesisDtoPreviousDiagnoseStatus.YES &&
                    previousDiagnoseDateUnknownValue === false,
                }}
              />
              {errors.previousDiagnoseDate && (
                <FormHelperText>Datum der Diagnose muss angegeben werden </FormHelperText>
              )}
            </FormControl>
            <div>
              <Controller
                name={previousDiagnoseDateUnknown}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch
                    disabled={previousDiagnoseValue !== ClinicalReportAnamnesisDtoPreviousDiagnoseStatus.YES}
                    checked={value}
                    onChange={onChange}
                    endDecorator={'Datum unbekannt'}
                  />
                )}
              />
            </div>
          </Stack>
        </Stack>
        <Divider orientation={'vertical'} />
        <Stack flex={1} gap={2}>
          <FormControl>
            <FormLabel>
              <Typography>Frühere Behandlung mit Antituberkulotika?</Typography>
            </FormLabel>
            <Controller
              name={previousTherpy}
              control={control}
              render={({ field: { onChange, value } }) => (
                <ToggleButtonGroup spacing={1} value={value}>
                  {Object.values(ClinicalReportAnamnesisDtoPreviousTherapyStatus).map((status) => (
                    <Button value={status} key={status} onClick={() => onChange(status)}>
                      {getPreviousTherapyStatusName(status)}
                    </Button>
                  ))}
                </ToggleButtonGroup>
              )}
            />
            <FormHelperText>Während mehr als 1 Monat</FormHelperText>
          </FormControl>
          <Stack gap={1}>
            <FormControl error={!!errors.previousTherpyDate}>
              <FormLabel>
                <Typography>Datum der Therapie</Typography>
              </FormLabel>
              <GermanDateInput
                control={control}
                name={previousTherpyDate}
                disabled={
                  previousTherapyValue !== ClinicalReportAnamnesisDtoPreviousTherapyStatus.YES ||
                  previousTherapyDateUnknownValue === true
                }
                rules={{
                  required:
                    previousTherapyValue === ClinicalReportAnamnesisDtoPreviousTherapyStatus.YES &&
                    previousTherapyDateUnknownValue === false &&
                    (!!startPhase1Value ||
                      (Array.isArray(therapyMedicationValue)
                        ? therapyMedicationValue.length > 0
                        : !!therapyMedicationValue)),
                }}
              />
              {errors.previousTherpyDate && <FormHelperText>Datum der Therapie muss angegeben werden</FormHelperText>}
            </FormControl>
            <div>
              <Controller
                name={previousTherpyDateUnknown}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Switch
                    disabled={previousTherapyValue !== ClinicalReportAnamnesisDtoPreviousTherapyStatus.YES}
                    checked={value}
                    onChange={onChange}
                    endDecorator={'Datum unbekannt'}
                  />
                )}
              />
            </div>
          </Stack>
        </Stack>
      </Stack>
      <Divider inset={'context'} />
      <Typography level={'title-lg'}>Aktuelle Therapie</Typography>
      <Typography color={'danger'}>
        Achtung! Beim Ändern dieser Felder wird die Therapiedauer auch unter &quot;Therapie&quot; überschrieben!
      </Typography>
      <Stack direction={'row'} gap={2}>
        <FormControl sx={{ flex: 1 }} error={!!errors.startPhase1}>
          <FormLabel>
            <Typography>Datum Therapiebeginn {isStartPhase1Required && '*'}</Typography>
          </FormLabel>
          <GermanDateInput
            control={control}
            name={startPhase1}
            rules={{
              required: isStartPhase1Required,
            }}
          />
          {errors.startPhase1 && <FormHelperText>Therapiebeginn muss ausgefüllt werden</FormHelperText>}
        </FormControl>
        <FormControl sx={{ flex: 1 }} error={!!errors.therapyEnd}>
          <FormLabel>
            <Typography>Datum geplanter Therapieabschluss</Typography>
          </FormLabel>
          <GermanDateInput
            control={control}
            name={therapyEnd}
            rules={{
              required: false,
              ...(minTherapyEnd ? { min: { value: minTherapyEnd, message: 'range' } } : {}),
            }}
            min={minTherapyEnd}
          />
          {errors.therapyEnd && <FormHelperText>Therapieabschluss muss nach dem Therapiebeginn liegen</FormHelperText>}
        </FormControl>
      </Stack>
      <FormControl>
        <FormLabel>
          <Typography>Medikamente bei Beginn der Behandlung</Typography>
        </FormLabel>
        <Controller
          name={therapyMedication}
          control={control}
          render={({ field: { onChange, value } }) => <SelectMedication onChange={onChange} value={value} />}
        />
      </FormControl>
      <FormControl>
        <FormLabel>
          <Typography>Tod vor Therapiebeginn?</Typography>
        </FormLabel>
        <Controller
          name={death}
          control={control}
          render={({ field: { onChange, value } }) => (
            <ToggleButtonGroup value={value ? 'YES' : 'NO'} spacing={1}>
              <Button value={'YES'} onClick={() => onChange(true)}>
                Ja
              </Button>
              <Button value={'NO'} onClick={() => onChange(false)}>
                Nein
              </Button>
            </ToggleButtonGroup>
          )}
        />
        <FormHelperText>Nur falls keine Behandlung</FormHelperText>
      </FormControl>
      {children}
    </Stack>
  );
}
